import { useRef, useEffect, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

export const AnimatedLineSection = ({ color, pathData, title, description, textAlign = 'left' }) => {
  const containerRef = useRef(null);
  const pathRef = useRef(null);
  const [pathLength, setPathLength] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  // Check if we're on mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // This hook tracks when the section enters and leaves the viewport
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"],
  });

  // Measures the path's length once it's rendered
  useEffect(() => {
    if (pathRef.current) {
      setPathLength(pathRef.current.getTotalLength());
    }
  }, []);

  // Maps scroll progress to the drawing animation of the line
  const strokeDashoffset = useTransform(scrollYProgress, [0.1, 0.9], [pathLength, 0]);

  // Responsive values
  const strokeWidth = isMobile ? "4rem" : "6rem";
  const titleFontSize = isMobile ? "2.5rem" : "5rem"; // Reduced from 3.5rem/8rem
  const descriptionFontSize = isMobile ? "1rem" : "2rem"; // Reduced from 1.2rem/3rem
  const descriptionMaxWidth = isMobile ? "90%" : "1200px";
  const containerPadding = isMobile ? "20px" : "40px";
  const sectionHeight = isMobile ? "50vh" : "100vh";

  // Expanded viewBox to accommodate the deeper descent of the path
  const viewBox = "0 0 4000 2200";

  // Determine alignment and padding based on textAlign prop
  const justifyContent = textAlign === 'right' ? 'flex-end' : 'flex-start';
  const paddingSide = textAlign === 'right' ? 'paddingRight' : 'paddingLeft';
  const paddingValue = isMobile ? '20px' : '80px';

  return (
    // 3. The container is now a standard div. No more 'sticky' positioning.
    <div
      ref={containerRef}
      style={{
        height: sectionHeight,
        background: '#FFFFFF',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: justifyContent, // Dynamic based on textAlign prop
        overflow: 'hidden',
        padding: containerPadding,
        boxSizing: 'border-box',
      }}
    >
      {/* The SVG is an absolute overlay within this section */}
      <motion.svg
        viewBox={viewBox}
        preserveAspectRatio="xMidYMid meet"
        style={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          zIndex: 0,
        }}
      >
        <motion.path
          ref={pathRef}
          d={pathData}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={pathLength}
          strokeDashoffset={strokeDashoffset}
        />
      </motion.svg>

      {/* The text content is dynamically aligned based on textAlign prop */}
      <div style={{
        position: 'relative',
        zIndex: 2,
        textAlign: textAlign, // Dynamic based on prop
        color: '#222222',
        fontFamily: 'PPNeueMontreal, sans-serif',
        width: '100%',
        maxWidth: '1200px',
        [paddingSide]: paddingValue, // Dynamic padding based on alignment
      }}>
        <h2 style={{
          fontSize: titleFontSize,
          fontWeight: 500,
          marginBottom: isMobile ? '0.5rem' : '1rem',
          lineHeight: 1.1,
          marginTop: 0,
        }}>
          {title}
        </h2>
        <p style={{
          fontSize: descriptionFontSize,
          fontWeight: 300,
          maxWidth: descriptionMaxWidth,
          margin: '0', // Changed from '0 auto' to '0' for left alignment
          lineHeight: 1.4,
        }}>
          {description}
        </p>
      </div>
    </div>
  );
};